import { toastActions, TOAST_STATUS } from '@leagueplatform/toast-messages';
import {
  DEFAULT_PARTNER_ID,
  BENEFIT_BOOKLET_DOWNLOAD_URL_MARKER,
} from 'common/constants';
import {
  LINK_TYPES,
  QUERY_PARAM_KEYS,
  SSO_READY_LISTENER_TIMEOUT,
} from 'common/sso-constants';
import { isAbsoluteUrl } from '@leagueplatform/web-common';
import { isPhoneOrMailHref } from 'utils/is-phone-or-mail-href';
import { useAuthedWebviewUrlStore } from 'store/authed-webview-link-store';
import {
  PartnerDefaultOverrideValues,
  sendAuthTokenIfReady,
} from '../post-message-auth-helpers';
import { logger } from '../logger';
import { captureSsoError } from '../sentry-helpers';
import { getDefaultOverrideValues } from './get-default-override-values';
import { handleBenefitBookletDownload } from './handle-benefit-booklet-download';

export class LinkHandlerClass {
  // eslint-disable-next-line class-methods-use-this
  handleLink = async (urlString: string, target?: string) => {
    // if the url is the benefits booklet download link, handle benefit booklet download and then exit
    if (urlString.startsWith(BENEFIT_BOOKLET_DOWNLOAD_URL_MARKER)) {
      await handleBenefitBookletDownload(urlString);
      return;
    }

    // Open link without opening new tab if tel: or mailto:
    if (isPhoneOrMailHref(urlString)) {
      window.location.assign(urlString);
      return;
    }

    // Must be an external URL, or else show error
    if (!isAbsoluteUrl(urlString)) {
      toastActions.add({
        type: TOAST_STATUS.ERROR,
        textId: 'EXTERNAL_URL_REDIRECT_FAILED',
      });
      captureSsoError(new Error('Target URL is not absolute url.'), urlString);
      return;
    }
    const url = new URL(urlString);

    // Parse query params for required identifiers, then remove from URL
    const linkType = (
      url.searchParams.get(QUERY_PARAM_KEYS.linkType) || ''
    ).toLowerCase();
    url.searchParams.delete(QUERY_PARAM_KEYS.linkType);

    const partnerId =
      url.searchParams.get(QUERY_PARAM_KEYS.partnerId) || DEFAULT_PARTNER_ID;
    url.searchParams.delete(QUERY_PARAM_KEYS.partnerId);

    // Parse query params for optional identifiers used only for specific SSOs, then remove from URL
    const groupNumber = url.searchParams.get(QUERY_PARAM_KEYS.groupNumber);
    if (groupNumber) url.searchParams.delete(QUERY_PARAM_KEYS.groupNumber);

    const clientNumber = url.searchParams.get(QUERY_PARAM_KEYS.clientNumber);
    if (clientNumber) url.searchParams.delete(QUERY_PARAM_KEYS.clientNumber);

    const brandUbk = url.searchParams.get(QUERY_PARAM_KEYS.brandUbk);
    if (brandUbk) url.searchParams.delete(QUERY_PARAM_KEYS.brandUbk);

    const searchType = url.searchParams.get(QUERY_PARAM_KEYS.searchType);
    if (searchType) url.searchParams.delete(QUERY_PARAM_KEYS.searchType);

    switch (linkType) {
      case LINK_TYPES.AUTHED_WEBVIEW: {
        useAuthedWebviewUrlStore.getState().setUrl(url.toString());
        break;
      }
      case LINK_TYPES.SSO: {
        const newWindow = window.open(url, '_blank');

        if (!newWindow) {
          toastActions.add({
            type: TOAST_STATUS.ERROR,
            textId: 'EXTERNAL_URL_REDIRECT_FAILED',
          });
          captureSsoError(
            new Error('Target SSO new window does not exist.'),
            urlString,
          );
          break; // if the new window does not exist, break to avoid checking auth and the message handler
        }

        let isAuthSuccessful = false;

        const messageHandler = async (event: MessageEvent) => {
          logger.log('Received message', event);

          let defaultOverrideValues: PartnerDefaultOverrideValues | undefined;
          try {
            defaultOverrideValues = await getDefaultOverrideValues();

            // Add/override any optional values if they come directly from the SSO url query params
            if (groupNumber) defaultOverrideValues.groupNumber = groupNumber;
            if (clientNumber) defaultOverrideValues.clientNumber = clientNumber;
            if (brandUbk) defaultOverrideValues.brandUbk = brandUbk;
            if (searchType) defaultOverrideValues.searchType = searchType;
          } catch (e: any) {
            captureSsoError(e instanceof Error ? e : new Error(e), urlString);
          }

          try {
            const isAuthenticated = await sendAuthTokenIfReady(
              event,
              url.origin,
              newWindow,
              partnerId,
              defaultOverrideValues,
            );

            if (isAuthenticated) {
              isAuthSuccessful = true;
              window.removeEventListener('message', messageHandler);
            }
          } catch (e: any) {
            toastActions.add({
              type: TOAST_STATUS.ERROR,
              textId: 'EXTERNAL_URL_REDIRECT_FAILED',
            });
            captureSsoError(e instanceof Error ? e : new Error(e), urlString);
          }
        };
        window.addEventListener('message', messageHandler);
        // Remove event listener after a set time in case no event is received.
        setTimeout(() => {
          if (!isAuthSuccessful) {
            window.removeEventListener('message', messageHandler);
            captureSsoError(
              new Error(
                `Could not send auth token, ready message not received within ${SSO_READY_LISTENER_TIMEOUT}ms from new window.`,
              ),
              urlString,
            );
            toastActions.add({
              type: TOAST_STATUS.ERROR,
              textId: 'EXTERNAL_URL_REDIRECT_FAILED',
            });
          }
        }, SSO_READY_LISTENER_TIMEOUT);

        break;
      }
      default:
        window.open(url, target || '_blank', 'noopener,noreferrer');
    }
  };
}

export const LinkHandler = new LinkHandlerClass();
